/**
 * The client list — the practice app's spine, and the one screen the platform
 * half owns end to end.
 *
 * Every action is gated at the **UI** level here by the same verb the service
 * enforces at the **API** level (FiveOX ADR-0029). Both, always: a hidden
 * button is not a permission check, and an enforced API with no hidden button
 * teaches users the product is broken.
 */

import { Plus, Search } from 'lucide-react'
import { Link } from 'react-router'
import { useCallback, useEffect, useState } from 'react'

import { CLIENT, useEvaluation } from '../lib/access'
import { ApiError, type ClientRecord, clients } from '../lib/api'
import { NewClientDialog } from './NewClientDialog'

export function Clients() {
  const { can } = useEvaluation()

  const [rows, setRows] = useState<ClientRecord[]>([])
  const [total, setTotal] = useState(0)
  const [search, setSearch] = useState('')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [creating, setCreating] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const body = await clients.list({ search: search || undefined })
      setRows(body.data)
      setTotal(Number(body.meta?.total ?? body.data.length))
    } catch (err) {
      // The service sends a machine code; render what it means, not "error".
      setError(err instanceof ApiError ? err.message : 'The client list could not be loaded.')
    } finally {
      setLoading(false)
    }
  }, [search])

  useEffect(() => {
    // Debounced: a keystroke per request turns a search box into a load test.
    const timer = setTimeout(() => void load(), search ? 300 : 0)
    return () => clearTimeout(timer)
  }, [load, search])

  return (
    <div>
      <header className="mb-6 flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold text-foreground">Clients</h1>
          <p className="text-sm text-muted-foreground">
            {total === 1 ? '1 taxpayer' : `${total} taxpayers`} you reconcile for
          </p>
        </div>

        {can(CLIENT, 'can_create') && (
          <button
            type="button"
            onClick={() => setCreating(true)}
            className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground"
          >
            <Plus size={16} /> Add client
          </button>
        )}
      </header>

      <div className="mb-4 flex items-center gap-2 rounded-lg border border-border bg-surface-raised px-3 py-2">
        <Search size={16} className="text-muted-foreground" />
        <input
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Search by name or PAN"
          className="w-full bg-transparent text-sm outline-none"
          aria-label="Search clients"
        />
      </div>

      {error && (
        <p className="rounded-lg border border-border bg-surface-raised p-4 text-sm text-foreground">
          {error}
        </p>
      )}

      {loading && !error && <p className="text-sm text-muted-foreground">Loading…</p>}

      {!loading && !error && rows.length === 0 && (
        <div className="rounded-2xl border border-dashed border-border p-10 text-center">
          <p className="text-sm text-muted-foreground">
            {search ? 'No client matches that search.' : 'No clients yet.'}
          </p>
        </div>
      )}

      {!loading && rows.length > 0 && (
        <div className="overflow-x-auto rounded-2xl border border-border">
          <table className="w-full text-left text-sm">
            <thead className="border-b border-border text-xs uppercase text-muted-foreground">
              <tr>
                <th className="px-4 py-3 font-medium">Legal name</th>
                <th className="px-4 py-3 font-medium">Status</th>
                {/* No PAN column, deliberately (spec FR-18): the list payload
                    does not carry it, so the screen cannot leak what it never
                    received. The single-client view shows it to editors. */}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id} className="border-b border-border last:border-0 hover:bg-muted/50">
                  <td className="px-4 py-3 text-foreground">
                    <Link to={`/w/${row.id}`} className="font-medium text-primary hover:underline">
                      {row.legal_name}
                    </Link>
                    {row.trade_name && (
                      <span className="block text-xs text-muted-foreground">{row.trade_name}</span>
                    )}
                  </td>
                  <td className="px-4 py-3 capitalize text-muted-foreground">{row.status}</td>
                  <td className="px-4 py-3 text-right">
                    <Link
                      to={`/w/${row.id}`}
                      className="rounded-md border border-border px-2.5 py-1 text-xs font-medium hover:bg-muted"
                    >
                      Open workspace
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {creating && (
        <NewClientDialog
          onClose={() => setCreating(false)}
          onCreated={() => {
            setCreating(false)
            void load()
          }}
        />
      )}
    </div>
  )
}
